import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { flushSync } from "react-dom";
import api from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = anon
  const [loading, setLoading] = useState(true);

  const bootstrap = useCallback(async () => {
    const token = localStorage.getItem("rescape_token");
    if (!token) {
      setUser(false);
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      localStorage.removeItem("rescape_token");
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    bootstrap();
  }, [bootstrap]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    localStorage.setItem("rescape_token", data.token);
    // Force the user update to commit before the caller navigates —
    // avoids a race where ProtectedRoute reads stale user=false.
    flushSync(() => { setUser(data.user); });
    return data.user;
  };

  const register = async (name, email, password, ref) => {
    const { data } = await api.post("/auth/register", { name, email, password, ref });
    localStorage.setItem("rescape_token", data.token);
    flushSync(() => { setUser(data.user); });
    return data.user;
  };

  const logout = () => {
    localStorage.removeItem("rescape_token");
    setUser(false);
  };

  const forgotPassword = async (email) => {
    const { data } = await api.post("/auth/forgot-password", { email });
    return data;
  };

  const resetPassword = async (token, password) => {
    const { data } = await api.post("/auth/reset-password", { token, password });
    return data;
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, forgotPassword, resetPassword }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
